import { Hand, History, MessageSquare, Plus } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode
} from "react";

import { workflowDisplayText } from "@/features/caseIde/workflows/catalog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { TabCloseButton } from "../../components/TabCloseButton";
import type { CaseAgentThread } from "../api/caseAgentApi";
import { plainMentions } from "../mentions";
import { ThreadMenuItem } from "./ThreadMenuItem";

type Props = {
  /** Every conversation on the case, for the history menu. */
  threads: CaseAgentThread[];
  /** The tabs, in strip order (see `useOpenThreadTabs`). */
  openThreads: CaseAgentThread[];
  activeId: string | null;
  /** Unread mention notifications per thread id (from the notifications feature). */
  unreadByThread?: Record<string, number>;
  disabled?: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  onClose: (id: string) => void;
  onCloseOthers: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
  /** "Save as automation…": open the automation builder seeded from this conversation. */
  onSaveAsSkill?: (id: string) => void;
  /** Threads homed elsewhere that worked on this case; each opens at its home. */
  elsewhere?: CaseAgentThread[];
  onOpenElsewhere?: (thread: CaseAgentThread) => void;
  /** Panel-level controls after history (the IDE's Maximize Casey). */
  actions?: ReactNode;
};

const UnreadBadge: FC<{ count: number; testId: string; what: string }> = ({
  count,
  testId,
  what
}) =>
  count > 0 ? (
    <Badge
      variant="default"
      className="h-4 min-w-4 shrink-0 px-1 text-[10px] tabular-nums"
      data-testid={testId}
      aria-label={`${count} unread mentions ${what}`}
    >
      {count > 99 ? "99+" : count}
    </Badge>
  ) : null;

const IconButton: FC<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
  children: ReactNode;
}> = ({ label, onClick, disabled, testId, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="size-6 text-muted-foreground hover:text-foreground"
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

/** Inline title editor for a tab: Enter commits, Escape (or blur) cancels. */
const RenameInput: FC<{
  initial: string;
  onCommit: (title: string) => void;
  onCancel: () => void;
}> = ({ initial, onCommit, onCancel }) => {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const commit = () => {
    const next = value.trim();
    if (next && next !== initial) onCommit(next);
    else onCancel();
  };
  return (
    <input
      ref={ref}
      aria-label="Conversation title"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        else if (e.key === "Escape") onCancel();
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
      className="h-5 min-w-0 flex-1 rounded-sm border border-ring bg-background px-1 text-xs outline-none"
    />
  );
};

type TabProps = {
  thread: CaseAgentThread | null;
  active: boolean;
  unread: number;
  renaming: boolean;
  onSelect: () => void;
  onClose?: () => void;
  onCloseOthers?: () => void;
  onRename?: () => void;
  onRenameCommit: (title: string) => void;
  onRenameCancel: () => void;
  onDelete?: () => void;
  onSaveAsSkill?: () => void;
};

const Tab: FC<TabProps> = ({
  thread,
  active,
  unread,
  renaming,
  onSelect,
  onClose,
  onCloseOthers,
  onRename,
  onRenameCommit,
  onRenameCancel,
  onDelete,
  onSaveAsSkill
}) => {
  const title =
    plainMentions(workflowDisplayText(thread?.title ?? "")) || "New Chat";
  // A rename picked from the context menu starts once the menu has closed (see
  // `onCloseAutoFocus` below): the menu still traps focus while it is closing
  // and would pull it straight back out of the new input.
  const renameAfterMenu = useRef(false);
  const onMiddleClick = (e: MouseEvent) => {
    if (e.button === 1 && onClose) {
      e.preventDefault();
      onClose();
    }
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (renaming) return;
    if (e.key === "Enter" || e.key === " ") onSelect();
    else if (e.key === "Delete" && onClose) onClose();
  };
  const body = (
    <div
      role="tab"
      aria-selected={active}
      tabIndex={0}
      title={title}
      data-testid="chat-tab"
      onClick={onSelect}
      onDoubleClick={onRename}
      onAuxClick={onMiddleClick}
      onKeyDown={onKeyDown}
      className={cn(
        // Unlike editor tabs these shrink to fit: the agent panel is narrow and
        // usually holds one or two chats, so titles truncate before the strip scrolls.
        "group flex h-8 max-w-56 min-w-16 cursor-pointer items-center gap-1.5 border-r border-border py-0 pl-3 text-xs select-none",
        onClose ? "pr-2" : "pr-3",
        // Inactive tabs give up their space so the open chat keeps its title.
        active
          ? "shrink-0 bg-background text-foreground shadow-[inset_0_-2px_0_0_var(--color-primary)]"
          : "shrink bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      <MessageSquare className="size-3.5 shrink-0 opacity-70" />
      {renaming ? (
        <RenameInput
          initial={title}
          onCommit={onRenameCommit}
          onCancel={onRenameCancel}
        />
      ) : (
        <span className="truncate">{title}</span>
      )}
      {thread?.pending_approval || thread?.pending_question ? (
        <Hand
          className="size-3 shrink-0 text-destructive"
          aria-label={
            thread.pending_approval
              ? "Awaiting approval"
              : "Waiting on your answer"
          }
        />
      ) : null}
      <UnreadBadge count={unread} testId="tab-unread-badge" what="here" />
      {onClose ? (
        <TabCloseButton
          label={`Close ${title}`}
          active={active}
          onClick={onClose}
        />
      ) : null}
    </div>
  );
  if (!thread) return body;
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{body}</ContextMenuTrigger>
      <ContextMenuContent
        onCloseAutoFocus={(e) => {
          if (!renameAfterMenu.current) return;
          renameAfterMenu.current = false;
          e.preventDefault();
          onRename?.();
        }}
      >
        <ContextMenuItem onSelect={onClose}>Close</ContextMenuItem>
        <ContextMenuItem onSelect={onCloseOthers}>Close others</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          onSelect={() => {
            renameAfterMenu.current = true;
          }}
        >
          Rename
        </ContextMenuItem>
        {onSaveAsSkill && (thread?.message_count ?? 0) > 0 ? (
          <ContextMenuItem onSelect={onSaveAsSkill} data-testid="save-as-skill">
            Save as automation…
          </ContextMenuItem>
        ) : null}
        <ContextMenuItem variant="destructive" onSelect={onDelete}>
          Delete conversation
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

/**
 * The agent panel's top bar, in the shape of Cursor's chat header: a strip of
 * open chat tabs, then new chat and history. The tabs are a subset of the
 * conversations; the history menu lists them all. Rename, close-others and
 * delete live on each tab's context menu (and double-click renames).
 */
export const AgentPanelHeader: FC<Props> = ({
  threads,
  openThreads,
  activeId,
  unreadByThread = {},
  disabled,
  onSelect,
  onNew,
  onClose,
  onCloseOthers,
  onRename,
  onDelete,
  onSaveAsSkill,
  elsewhere = [],
  onOpenElsewhere,
  actions
}) => {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<CaseAgentThread | null>(null);
  const openIds = new Set(openThreads.map((t) => t.id));
  // Mentions waiting in conversations that are not open as tabs.
  const historyUnread = Object.entries(unreadByThread).reduce(
    (sum, [id, n]) => (openIds.has(id) ? sum : sum + n),
    0
  );
  const history = [...threads].sort((a, b) =>
    (b.updated_at ?? "").localeCompare(a.updated_at ?? "")
  );
  const tabFor = (thread: CaseAgentThread | null) => {
    const id = thread?.id ?? null;
    return (
      <Tab
        key={id ?? "new"}
        thread={thread}
        active={id === activeId}
        unread={id ? unreadByThread[id] ?? 0 : 0}
        renaming={id !== null && renaming === id}
        onSelect={() => id && onSelect(id)}
        onClose={id ? () => onClose(id) : undefined}
        onCloseOthers={id ? () => onCloseOthers(id) : undefined}
        onRename={id ? () => setRenaming(id) : undefined}
        onRenameCommit={(title) => {
          if (id) onRename(id, title);
          setRenaming(null);
        }}
        onRenameCancel={() => setRenaming(null)}
        onDelete={thread ? () => setConfirm(thread) : undefined}
        onSaveAsSkill={
          thread && onSaveAsSkill ? () => onSaveAsSkill(thread.id) : undefined
        }
      />
    );
  };

  return (
    <div
      className="flex h-8 w-full shrink-0 items-stretch border-b border-border bg-muted/40"
      data-testid="agent-panel-header"
    >
      <div
        role="tablist"
        aria-label="Conversations"
        className="flex min-w-0 flex-1 items-stretch overflow-x-auto [scrollbar-width:thin]"
      >
        {openThreads.map(tabFor)}
        {activeId === null || openThreads.length === 0 ? tabFor(null) : null}
      </div>
      <div className="flex shrink-0 items-center gap-0.5 px-1">
        <IconButton
          label="New Chat"
          onClick={onNew}
          disabled={disabled}
          testId="chat-new"
        >
          <Plus className="size-3.5" />
        </IconButton>

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative size-6 text-muted-foreground hover:text-foreground"
                  aria-label="Conversation history"
                  data-testid="chat-history"
                  disabled={disabled}
                >
                  <History className="size-3.5" />
                  {historyUnread > 0 ? (
                    <Badge
                      variant="default"
                      className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px] tabular-nums"
                      data-testid="history-unread-badge"
                      aria-label={`${historyUnread} unread mentions in other conversations`}
                    >
                      {historyUnread > 99 ? "99+" : historyUnread}
                    </Badge>
                  ) : null}
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">Conversation history</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="text-[11px] text-muted-foreground">
              Conversations
            </DropdownMenuLabel>
            {history.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                No conversations yet.
              </div>
            ) : null}
            {history.map((t) => (
              <ThreadMenuItem
                key={t.id}
                thread={t}
                active={t.id === activeId}
                unread={unreadByThread[t.id] ?? 0}
                onSelect={() => onSelect(t.id)}
                onDelete={() => setConfirm(t)}
              />
            ))}
            {elsewhere.length > 0 && onOpenElsewhere ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-sm font-normal text-fg-3">
                  From Other Conversations
                </DropdownMenuLabel>
                {elsewhere.map((t) => (
                  <ThreadMenuItem
                    key={t.id}
                    thread={t}
                    active={false}
                    unread={unreadByThread[t.id] ?? 0}
                    onSelect={() => onOpenElsewhere(t)}
                  />
                ))}
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
        {actions}
      </div>

      <AlertDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{confirm?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              The transcript is removed. Work the agent already did on the case
              stays.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (confirm) onDelete(confirm.id);
                setConfirm(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
